/**
 * ForumMain Component - Main forum interface with thread listing and navigation
 *
 * This component provides the primary interface for trip discussion forums,
 * including thread browsing, creation, and navigation between discussions.
 *
 * Features:
 * - Thread listing with pagination and sorting
 * - Thread creation interface
 * - Category filtering and search
 * - User management and permissions
 * - Real-time updates for new threads and messages
 */

import React, { useState, useEffect, useCallback } from 'react';
import { forumService } from '../../services/forumService.js';
import { eventBridgeService } from '@trip-planner-monorepo/shared';

const ForumMain = ({ tripId, userId, userRole, onThreadSelect, onCreateThread }) => {
  // Component state
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forum, setForum] = useState(null);
  const [filters, setFilters] = useState({
    category: 'all',
    sortBy: 'lastActivity',
    sortOrder: 'desc',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    hasMore: false
  });
  const [showCreateThread, setShowCreateThread] = useState(false);

  /**
   * Initialize forum and load threads
   */
  useEffect(() => {
    initializeForum();
    loadThreads();

    // Subscribe to real-time updates
    const unsubscribeThread = eventBridgeService.subscribe('THREAD_CREATED', handleNewThread);
    const unsubscribeMessage = eventBridgeService.subscribe('MESSAGE_POSTED', handleNewMessage);

    return () => {
      unsubscribeThread();
      unsubscribeMessage();
    };
  }, [tripId, userId]);

  /**
   * Reload threads when filters change
   */
  useEffect(() => {
    loadThreads();
  }, [filters, pagination.page]);

  /**
   * Initialize forum for trip
   */
  const initializeForum = useCallback(async () => {
    try {
      setLoading(true);
      const forumData = await forumService.createTripForum(tripId, userId);
      setForum(forumData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [tripId, userId]);

  /**
   * Load forum threads with current filters
   */
  const loadThreads = useCallback(async () => {
    if (!forum) return;

    try {
      setLoading(true);
      setError(null);

      const options = {
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      };

      if (filters.category === 'all') {
        delete options.category;
      }

      const result = await forumService.getForumThreads(tripId, options);

      setThreads(result.threads);
      setPagination(prev => ({
        ...prev,
        total: result.total,
        hasMore: result.hasMore
      }));

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [tripId, forum, filters, pagination.page, pagination.limit]);

  /**
   * Handle new thread creation event
   */
  const handleNewThread = useCallback((event) => {
    if (event.payload.tripId === tripId) {
      // Refresh thread list to show new thread
      loadThreads();
    }
  }, [tripId, loadThreads]);

  /**
   * Handle new message event
   */
  const handleNewMessage = useCallback((event) => {
    if (event.payload.tripId === tripId) {
      // Update thread last activity time
      setThreads(prev => prev.map(thread => {
        if (thread.id === event.payload.threadId) {
          return {
            ...thread,
            lastActivity: event.payload.timestamp,
            statistics: {
              ...thread.statistics,
              messageCount: thread.statistics.messageCount + 1
            }
          };
        }
        return thread;
      }));
    }
  }, [tripId]);

  /**
   * Handle filter changes
   */
  const handleFilterChange = useCallback((filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  }, []);

  /**
   * Handle thread creation
   */
  const handleCreateThread = useCallback(async (threadData) => {
    try {
      const threadId = await forumService.createThread(tripId, userId, threadData);
      setShowCreateThread(false);
      onCreateThread?.(threadId);
      loadThreads(); // Refresh list
    } catch (err) {
      setError(err.message);
    }
  }, [tripId, userId, onCreateThread, loadThreads]);

  /**
   * Handle thread selection
   */
  const handleThreadClick = useCallback((thread) => {
    onThreadSelect?.(thread);
  }, [onThreadSelect]);

  /**
   * Format last activity time
   */
  const formatLastActivity = (timestamp) => {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffMs = now - activityTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return activityTime.toLocaleDateString();
  };

  /**
   * Get category display name
   */
  const getCategoryName = (category) => {
    const categoryNames = {
      general: 'General Discussion',
      accommodation: 'Accommodation',
      activities: 'Activities & Places',
      transportation: 'Transportation',
      budget: 'Budget & Expenses',
      planning: 'Trip Planning'
    };
    return categoryNames[category] || category;
  };

  /**
   * Check if user can create threads
   */
  const canCreateThreads = () => {
    return forumService.hasPermission(userId, tripId, 'create_threads');
  };

  /**
   * Render thread creation form
   */
  const renderCreateThreadForm = () => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState('general');

    const handleSubmit = (e) => {
      e.preventDefault();
      handleCreateThread({ title, description, category });
    };

    return (
      <div className="create-thread-form">
        <h3>Create New Discussion</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Thread Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What would you like to discuss?"
              required
            />
          </div>

          <div className="form-group">
            <label>Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="general">General Discussion</option>
              <option value="accommodation">Accommodation</option>
              <option value="activities">Activities & Places</option>
              <option value="transportation">Transportation</option>
              <option value="budget">Budget & Expenses</option>
              <option value="planning">Trip Planning</option>
            </select>
          </div>

          <div className="form-group">
            <label>Description (Optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Provide more details about the discussion topic..."
              rows="3"
            />
          </div>

          <div className="form-actions">
            <button type="submit" className="btn primary">
              Create Thread
            </button>
            <button
              type="button"
              onClick={() => setShowCreateThread(false)}
              className="btn secondary"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  };

  if (loading && threads.length === 0) {
    return (
      <div className="forum-main loading">
        <div className="loading-spinner"></div>
        <p>Loading forum...</p>
      </div>
    );
  }

  return (
    <div className="forum-main">
      {/* Forum Header */}
      <div className="forum-header">
        <div className="forum-title">
          <h2>Trip Discussion Forum</h2>
          <span className="participant-count">
            {forum?.statistics.activeParticipants} participants
          </span>
        </div>

        {canCreateThreads() && (
          <button
            onClick={() => setShowCreateThread(true)}
            className="btn primary create-thread-btn"
          >
            + New Discussion
          </button>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
          <button onClick={() => setError(null)} className="error-close">×</button>
        </div>
      )}

      {/* Create Thread Form */}
      {showCreateThread && renderCreateThreadForm()}

      {/* Forum Controls */}
      <div className="forum-controls">
        <div className="filters">
          <select
            value={filters.category}
            onChange={(e) => handleFilterChange('category', e.target.value)}
            className="category-filter"
          >
            <option value="all">All Categories</option>
            <option value="general">General Discussion</option>
            <option value="accommodation">Accommodation</option>
            <option value="activities">Activities & Places</option>
            <option value="transportation">Transportation</option>
            <option value="budget">Budget & Expenses</option>
            <option value="planning">Trip Planning</option>
          </select>

          <select
            value={`${filters.sortBy}-${filters.sortOrder}`}
            onChange={(e) => {
              const [sortBy, sortOrder] = e.target.value.split('-');
              handleFilterChange('sortBy', sortBy);
              handleFilterChange('sortOrder', sortOrder);
            }}
            className="sort-filter"
          >
            <option value="lastActivity-desc">Latest Activity</option>
            <option value="createdAt-desc">Newest First</option>
            <option value="createdAt-asc">Oldest First</option>
            <option value="title-asc">Title A-Z</option>
          </select>
        </div>

        <div className="search">
          <input
            type="text"
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            placeholder="Search discussions..."
            className="search-input"
          />
        </div>
      </div>

      {/* Thread List */}
      <div className="thread-list">
        {threads.length === 0 ? (
          <div className="empty-forum">
            <div className="empty-icon">💬</div>
            <h3>No discussions yet</h3>
            <p>Start the conversation by creating the first discussion thread!</p>
            {canCreateThreads() && (
              <button
                onClick={() => setShowCreateThread(true)}
                className="btn primary"
              >
                Create First Discussion
              </button>
            )}
          </div>
        ) : (
          threads.map(thread => (
            <div
              key={thread.id}
              className={`thread-item ${thread.isPinned ? 'pinned' : ''}`}
              onClick={() => handleThreadClick(thread)}
            >
              <div className="thread-icon">
                {thread.isPinned && <span className="pin-icon">📌</span>}
                <span className="category-icon">
                  {thread.category === 'accommodation' && '🏨'}
                  {thread.category === 'activities' && '🎯'}
                  {thread.category === 'transportation' && '✈️'}
                  {thread.category === 'budget' && '💰'}
                  {thread.category === 'planning' && '📋'}
                  {thread.category === 'general' && '💬'}
                </span>
              </div>

              <div className="thread-content">
                <div className="thread-title">{thread.title}</div>
                <div className="thread-meta">
                  <span className="category">{getCategoryName(thread.category)}</span>
                  <span className="separator">•</span>
                  <span className="author">by {thread.createdBy}</span>
                  <span className="separator">•</span>
                  <span className="timestamp">{formatLastActivity(thread.lastActivity)}</span>
                </div>
                {thread.description && (
                  <div className="thread-description">{thread.description}</div>
                )}
              </div>

              <div className="thread-stats">
                <div className="stat">
                  <span className="stat-number">{thread.statistics.messageCount}</span>
                  <span className="stat-label">messages</span>
                </div>
                <div className="stat">
                  <span className="stat-number">{thread.statistics.participantCount}</span>
                  <span className="stat-label">participants</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination.hasMore && (
        <div className="forum-pagination">
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            disabled={loading}
            className="btn secondary load-more"
          >
            {loading ? 'Loading...' : 'Load More Discussions'}
          </button>
        </div>
      )}

      {/* Forum Statistics */}
      {forum && (
        <div className="forum-stats">
          <div className="stat-item">
            <span className="stat-value">{forum.statistics.totalThreads}</span>
            <span className="stat-label">Discussions</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{forum.statistics.totalMessages}</span>
            <span className="stat-label">Messages</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{forum.statistics.activeParticipants}</span>
            <span className="stat-label">Participants</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ForumMain;